import { Box, Button, Chip, TableCell, TableRow, Grid } from '@mui/material';
import { FC, Fragment, Reducer, SyntheticEvent, useCallback, useEffect, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// components
import NoDataFound from 'components/common/NoDataFound';
import TableComponent from 'components/common/TableComponent';
import TableContainer from 'components/common/TableContainer';
import TableLoader from 'components/common/TableLoader';
import TableSearchField from 'components/common/TableSearchField';
import TableTabsComponent from 'components/common/TableTabsComponent';
import AddOrgStaffDialog from './AddOrgStaffDialog';
// constants, reducers, graphql, styles
import {
  ACTIVE_TEXT,
  ADD_STAFF_TEXT,
  HTTP_STATUS,
  INACTIVE_TEXT,
  LOADING_TABLE_ROWS,
  ORG_STAFF_TABLE_HEADER,
  STAFF_ROUTE,
  STAFF_TABS,
  VIEW_USER_ROUTE,
} from 'constants/index';
import { UserStatus, UsersPayload, useFindAllOrgStaffLazyQuery } from 'generated/graphql';
import { OrgStaffTableProps, ParamType } from 'interfaces';
import { StaffTabType } from 'interfaces/TabTypes';
import { formatPhone, formatTimeStamp } from 'lib/helper';
import { Action, ActionType, State, initialState, staffReducer } from 'reducer/staffReducer';
import { cursorPointer, flexCenterBetween, textWhiteSpace } from 'styles/commonComponentStyle';

const OrgStaffTable: FC<OrgStaffTableProps> = ({ organizationId }) => {
  const navigate = useNavigate();
  const { id } = useParams<ParamType>();
  const orgId = organizationId ?? id ?? '';

  const [openAddStaff, setOpenAddStaff] = useState<boolean>(false);
  const [state, dispatch] = useReducer<Reducer<State, Action>>(staffReducer, initialState);
  const { page, rowsPerPage, search, count, data, activeTab } = state;

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const [findAllOrgStaff, { loading, error }] = useFindAllOrgStaffLazyQuery({
    onCompleted: (data) => {
      const { findAllOrgStaff } = data;
      const { pagination, response, data: staffData } = findAllOrgStaff || {};
      const { status } = response || {};

      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({ type: ActionType.SET_DATA, data: staffData as UsersPayload['data'] });
      } else {
        resetPage();
      }
    },

    onError: () => {
      resetPage();
    },
  });

  const fetchStaff = useCallback(async () => {
    if (!orgId) {
      return;
    }

    await findAllOrgStaff({
      variables: {
        findAllOrgStaffInput: {
          organizationId: orgId,
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          search,
          ...(activeTab !== 'all' && { status: activeTab }),
        },
      },
    });
  }, [findAllOrgStaff, orgId, search, rowsPerPage, page, activeTab]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const tabHandler = (_: SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as StaffTabType });
  };

  const onSearch = (val: string) => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_SEARCH, search: val });
  };

  const onClear = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_SEARCH, search: '' });
  };

  const noData = Boolean((!loading && data?.length === 0) || error);

  return (
    <Grid container>
      <Grid item xs={12} md={12} mt={4}>
        <TableContainer>
          <TableTabsComponent activeTab={activeTab} tabsList={STAFF_TABS} tabHandler={tabHandler} />

          <Box sx={{ ...flexCenterBetween, flexWrap: 'wrap', gap: 2, pr: { xs: 2, lg: 3 } }}>
            <Box flex={1}>
              <TableSearchField search={search} onClear={onClear} onChange={onSearch} />
            </Box>

            <Button variant="contained" size="small" onClick={() => setOpenAddStaff(true)}>
              {ADD_STAFF_TEXT}
            </Button>
          </Box>

          <TableComponent
            noData={noData}
            page={page - 1}
            count={count}
            rowsPerPage={rowsPerPage}
            tableHeader={ORG_STAFF_TABLE_HEADER}
            setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
            setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
            {loading ? (
              <TableLoader columns={6} rows={LOADING_TABLE_ROWS} />
            ) : (
              <Fragment>
                {data?.map((cell) => {
                  const { id, email, firstName, lastName, phone, createdAt, staffRole, status } = cell || {};

                  return (
                    <TableRow key={id}>
                      <TableCell
                        sx={{ ...textWhiteSpace, ...cursorPointer }}
                        onClick={() => navigate(`${VIEW_USER_ROUTE}/${orgId}${STAFF_ROUTE}/${id}`)}>
                        <b>
                          {firstName ?? '-'} {lastName ?? '-'}
                        </b>
                      </TableCell>
                      <TableCell sx={textWhiteSpace}>{staffRole ?? '--'}</TableCell>
                      <TableCell>{email ?? '--'}</TableCell>
                      <TableCell sx={textWhiteSpace}>{formatPhone(phone || '')}</TableCell>
                      <TableCell>{formatTimeStamp(createdAt, 'MM/DD/YY')}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={status === UserStatus.Active ? ACTIVE_TEXT : INACTIVE_TEXT}
                          color={status === UserStatus.Active ? 'success' : 'error'}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            )}
          </TableComponent>

          <NoDataFound noData={noData} />

          <AddOrgStaffDialog
            open={openAddStaff}
            fetch={fetchStaff}
            organizationId={orgId}
            onClose={() => setOpenAddStaff(false)}
          />
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default OrgStaffTable;
